import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, MapPin } from "@/lib/public-icons";
import { useMemo, useState } from "react";
import { SearchField } from "@/components/app/patterns/search-field";
import { Skeleton } from "@/components/ui/skeleton";
import { orpcPublic } from "@/lib/orpc";
import { JobCard } from "./-components/job-card";
import { JobsNotFound } from "@/routes/jobs-preview/$domain/-components/jobs-not-found";
import { JobsShell } from "./-components/jobs-shell";

export const Route = createFileRoute("/jobs-preview/$domain/")({
  component: JobsHome,
});

/** Every position a building has open, grouped the way it files them. */
function JobsHome() {
  const { domain } = Route.useParams();
  const [search, setSearch] = useState("");

  const page = useQuery({
    ...orpcPublic.careers.jobsByDomain.queryOptions({ input: { domain } }),
    retry: false,
  });

  const boards = useMemo(() => {
    const all = page.data?.boards ?? [];
    const query = search.trim().toLowerCase();
    if (!query) return all;
    return all
      .map((group) => ({
        ...group,
        jobs: group.jobs.filter((job) =>
          [job.title, job.department, job.location].some((field) =>
            field?.toLowerCase().includes(query),
          ),
        ),
      }))
      .filter((group) => group.jobs.length > 0);
  }, [page.data?.boards, search]);

  if (page.isError) {
    return (
      <JobsShell domain={domain}>
        <JobsNotFound />
      </JobsShell>
    );
  }

  const facilityName = page.data?.facility.name;
  const where = [page.data?.facility.city, page.data?.facility.state].filter(Boolean).join(", ");
  const total = (page.data?.boards ?? []).reduce((sum, group) => sum + group.jobs.length, 0);

  return (
    <JobsShell domain={domain} facilityName={facilityName}>
      <div className="mb-8">
        {page.isPending ? (
          <Skeleton className="mb-2 h-10 w-64" />
        ) : (
          <h1 className="text-3xl font-semibold tracking-tight">
            {facilityName ? `Join ${facilityName}` : "Open Positions"}
          </h1>
        )}
        {where ? (
          <p className="text-muted-foreground mt-1 flex items-center gap-1">
            <MapPin className="size-4" />
            {where}
          </p>
        ) : null}
        {page.isPending ? null : (
          <p className="text-muted-foreground mt-2">
            {total === 0
              ? "No open positions at this time."
              : `${total} open position${total === 1 ? "" : "s"}`}
          </p>
        )}
      </div>

      {total > 0 ? (
        <SearchField
          className="mb-6 sm:w-full"
          placeholder="Search positions…"
          value={search}
          onChange={setSearch}
        />
      ) : null}

      {page.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : boards.length === 0 ? (
        <div className="text-muted-foreground py-12 text-center">
          {search ? "No positions match your search." : "No open positions at this time."}
        </div>
      ) : (
        <div className="space-y-10">
          {boards.map((group) => (
            <section key={group.board.slug}>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">{group.board.name}</h2>
                  {group.board.description ? (
                    <p className="text-muted-foreground mt-0.5 text-sm">
                      {group.board.description}
                    </p>
                  ) : null}
                </div>
                <Link
                  className="text-primary flex items-center gap-1 text-sm hover:underline"
                  params={{ domain, boardSlug: group.board.slug }}
                  to="/jobs-preview/$domain/$boardSlug"
                >
                  View all <ChevronRight className="size-3" />
                </Link>
              </div>
              <div className="space-y-3">
                {group.jobs.map((job) => (
                  <JobCard boardSlug={group.board.slug} domain={domain} job={job} key={job.id} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </JobsShell>
  );
}
